
import { FileCheck, CreditCard, Package, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import type { ProcessStep } from "@/types/home";
import { cn } from "@/lib/utils";

const ProcessSteps = () => {
  const steps: ProcessStep[] = [
    {
      icon: FileCheck,
      title: "Get pre-approved",
      description: "Register with any of our partner associations or groups as an individual, business, community or government agency and verify your asset."
    }, 
    {
      icon: CreditCard,
      title: "Receive cheque",
      description: "Receive your credit limit cheque based on your asset equity."
    }, 
    {
      icon: Package,
      title: "Crowdfunding/Borrow & Shop",
      description: "Access crowdfunding loans, and shop from our partner merchants all with your Obotan credit guarantee cheque."
    }
  ];
  
  return (
    <section className="py-24 bg-gradient-to-br from-white to-indigo-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h3 className="text-lg font-medium text-indigo-600 mb-3 animate-in slide-in-from-bottom duration-500">HOW IT WORKS</h3>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-in slide-in-from-bottom duration-700 delay-100 bg-gradient-to-r from-indigo-800 to-purple-700 bg-clip-text text-transparent">
            Applying for the Obotan credit is as simple as 1, 2, 3
          </h2>
          
          <div className="flex flex-wrap justify-center gap-4 mt-8 mb-16 animate-in slide-in-from-bottom duration-700 delay-200">
            <Button variant="outline" className="rounded-full bg-white border-2 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 px-8 shadow-sm transition-all">
              ASSET BACKED
            </Button>
            <Button className="rounded-full bg-gradient-to-r from-indigo-700 to-indigo-900 text-white hover:from-indigo-800 hover:to-indigo-950 px-8 shadow-md transition-all">
              MAKING PAYMENTS
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-in slide-in-from-bottom duration-1000 delay-300">
          {steps.map((step, index) => (
            <div 
              key={index} 
              className={cn(
                "bg-white rounded-xl p-8 relative border border-indigo-100",
                "transform transition-all duration-500 hover:shadow-xl hover:-translate-y-2",
                "animate-in slide-in-from-bottom",
              )}
              style={{ animationDelay: `${300 + index * 100}ms` }}
            >
              <div className="absolute -top-4 left-8 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-md">
                Step {index + 1}
              </div>
              
              <div className="mb-8 bg-white rounded-lg shadow-md p-6 transform transition-all duration-300 hover:shadow-lg">
                {index === 0 && (
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <img 
                        src="/lovable-uploads/8606a715-f85b-4993-bcf3-59a3addef053.png" 
                        alt="Obotan Guarantee Cheque" 
                        className="w-full max-w-[280px] rounded-lg shadow-md transition-all duration-300 hover:shadow-lg" 
                      />
                    </div>
                    <p className="text-sm text-gray-500 mb-2">Start your application</p>
                    <p className="font-semibold text-lg mb-4">Fast pre-approval</p>
                    <Button 
                      size="sm" 
                      className="bg-gradient-to-r from-indigo-700 to-indigo-900 hover:from-indigo-800 hover:to-indigo-950 text-white text-xs rounded-full w-full"
                    >
                      Apply Now 
                      <ArrowRight className="ml-2 h-3 w-3" />
                    </Button>
                  </div>
                )}
                
                {index === 1 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center">
                          <Check className="h-4 w-4" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Application Submitted</p>
                        <div className="h-2 bg-green-500 rounded-full mt-1 w-full"></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center">
                          <Check className="h-4 w-4" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Verification Complete</p>
                        <div className="h-2 bg-green-500 rounded-full mt-1 w-full"></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center border-2 border-indigo-200">
                        <div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center">
                          <CreditCard className="h-4 w-4" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Cheque Generation</p>
                        <div className="h-2 bg-gray-200 rounded-full mt-1 w-full">
                          <div className="h-2 bg-indigo-600 rounded-full w-2/3 animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {index === 2 && (
                  <div className="text-center">
                    <p className="font-semibold mb-2">Ready to use</p>
                    <p className="text-sm text-gray-600">Accepted by all our partners</p>
                  </div>
                )}
              </div>
              
              <div>
                <p className="text-sm text-indigo-600 font-medium">STEP {index + 1}</p>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex flex-wrap justify-center gap-4 mt-16 animate-in slide-in-from-bottom duration-1000 delay-700">
          <Button 
            size="lg" 
            className="rounded-full px-8 text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg hover:shadow-indigo-200/50 transition-all duration-300"
          >
            APPLY NOW
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="rounded-full px-8 border-2 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-300"
          >
            LEARN MORE
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ProcessSteps;
